const removeNullishFields = (payload) => {
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === null) {
      delete payload[key];
    }
  });
};

const mapCompletedInputAssets = ({ uploadedAssets = [], maxInputUploads = 0 } = {}) => {
  const completedAssets = uploadedAssets
    .filter((asset) => asset.status === 'complete' && asset.url)
    .slice(0, maxInputUploads)
    .map((asset) => ({
      url: asset.url,
      type: asset.type || 'image',
      name: asset.name || asset.file?.name || null,
      mimeType: asset.mimeType || asset.file?.type || null,
      width: Number.isFinite(Number(asset.width)) ? Number(asset.width) : null,
      height: Number.isFinite(Number(asset.height)) ? Number(asset.height) : null,
      durationSeconds: Number.isFinite(Number(asset.durationSeconds)) ? Number(asset.durationSeconds) : null,
      fps: Number.isFinite(Number(asset.fps)) ? Number(asset.fps) : null,
      fileSizeBytes: Number.isFinite(Number(asset.fileSizeBytes ?? asset.file?.size))
        ? Number(asset.fileSizeBytes ?? asset.file?.size)
        : null,
      source: asset.source || null,
      sourceId: asset.sourceId || null,
      sourceTaskId: asset.sourceTaskId || null,
      sourceModelKey: asset.sourceModelKey || null,
      sourceApiProvider: asset.sourceApiProvider || null,
      sourceApiProviderSlug: asset.sourceApiProviderSlug || null,
      sourceApiProviderType: asset.sourceApiProviderType || null,
      sourceApiProviderModelName: asset.sourceApiProviderModelName || null,
      sourceResolution: asset.sourceResolution || null,
      sourceAspectRatio: asset.sourceAspectRatio || null,
    }));

  const uploadedImageUrls = completedAssets
    .filter((asset) => asset.type === 'image')
    .map((asset) => asset.url);

  return {
    completedAssets,
    uploadedImageUrls,
  };
};

export function buildStudioGenerationParams({
  promptText,
  currentQualityOptions = [],
  selectedResolution,
  selectedAspectRatio,
  selectedModel,
  selectedModelConfig,
  tokenOverride,
  turnstileToken,
  outputCount,
  currentDurationValue,
  functionMode,
  canToggleGenerateAudio,
  generateAudio,
  privacyUnrestricted,
  noContentFiltering,
  contextIrEnabled,
  canUseSeed,
  seedEnabled,
  seed,
  shouldApplyWebSearch,
  canUseFileInput,
  uploadedAssets = [],
  maxInputUploads = 0,
  imageQuality,
  background,
  moderation,
  grokMode,
  generationMode,
  fps,
} = {}) {
  const trimmedPrompt = String(promptText || '').trim();
  if (!trimmedPrompt) {
    return null;
  }

  const activeResolutionOption =
    currentQualityOptions.find((res) => res.id === selectedResolution)
    || currentQualityOptions[0];

  if (!activeResolutionOption || activeResolutionOption.isTemporarilyDisabled) {
    throw new Error('The selected resolution is temporarily unavailable for this model.');
  }

  const params = {
    prompt: trimmedPrompt,
    size: activeResolutionOption.apiSize,
    aspectRatioLabel: selectedAspectRatio || undefined,
    resolutionLabel: activeResolutionOption.resolutionLabel || undefined,
    modelName: selectedModelConfig?.displayName || selectedModel,
    modelKey: selectedModel,
    contextIrEnabled: selectedModel?.startsWith('minimax-h3') && generationMode !== 'unlimited' && contextIrEnabled === true,
    privacyUnrestricted: selectedModel?.startsWith('seedance-2.') && privacyUnrestricted === true,
    noContentFiltering: /^botgo-(?:video|image)-/.test(selectedModel || '') ? noContentFiltering : undefined,
    quality: activeResolutionOption.label,
    turnstileToken: tokenOverride || turnstileToken,
    outputCount: Number.isFinite(Number(outputCount)) ? Number(outputCount) : 1,
    imageQuality: imageQuality || undefined,
    background: background || undefined,
    moderation: moderation || undefined,
    grokMode: grokMode || undefined,
    generationMode: generationMode || undefined,
    ...(selectedModel?.startsWith('botgo-vsr-') ? { fps: Number(fps) || 30 } : {}),
  };

  if (selectedModelConfig?.type === 'video' && currentDurationValue) {
    params.duration = currentDurationValue;
    params.functionMode = functionMode;

    if (canToggleGenerateAudio) {
      params.generateAudio = generateAudio;
    }

    if (canUseSeed && seedEnabled) {
      params.seed = seed;
    }

    if (shouldApplyWebSearch) {
      params.webSearch = true;
    }
  }

  if (selectedModelConfig?.type === 'image') {
    params.functionMode = functionMode || 'general';
  }

  if (canUseFileInput && uploadedAssets.length > 0) {
    const { completedAssets, uploadedImageUrls } = mapCompletedInputAssets({
      uploadedAssets,
      maxInputUploads,
    });

    if (completedAssets.length > 0) {
      params.inputAssets = completedAssets;
      if (uploadedImageUrls.length > 0) {
        params.imageUrl = uploadedImageUrls;
      }
    }
  }

  removeNullishFields(params);
  return params;
}
